/* tslint:disable:no-trailing-whitespace import-spacing no-inferrable-types typedef-whitespace align */

import {ServiceManager} from "@/services/service.manager";

import {SimulationGroupElement} from './project/simulation.group.element';
import {ResultsElement}         from './project/results.element';
import {RootElement}            from './project/root.element';
import {LineSimJobElement}      from './project/line.sim.job.element';
import {KPIElement}             from './project/k.p.i.element';
import {KPITableElement}        from './project/k.p.i.table.element';

import {ProjectElement}        from './project/project.element';
import {ProjectElementType}     from './project/project.element.type';
import {ProjectElementFactory}  from './project.element.factory';
import {Blob}                   from './blob';

import {
  SimulationJob,
  SimulationJobType
}                              from '@/models/sim/simulation.job';

import {Logger}                from '@/util/logger';
import {isset, empty, dirname, errorAsText} from '@/util/util';

import {
  BehaviorSubject,
  forkJoin,
  Observable, of,
  Subscription
} from 'rxjs';

import {catchError, first} from 'rxjs/operators';
import {KPI} from "@/models/k.p.i";
import {SimulationElement} from "@/models/project/simulation.element";
import {ServiceLocator} from "@/services/service.locator";
import {AbstractProject} from "@/models/abstract.project";
import {ProjectType, projectTypeOfString, stringOfProjectType} from "@/models/project.type";
import {BatchSimJobElement} from "@/models/project/batch.sim.job.element";
import {OperationalMeasuresElement} from "@/models/project/operational.measures.element";
import {BatchSummariesElement} from "@/models/project/batch.summaries.element";
import {RegressionElement} from "@/models/project/regression.element";
import {BatchGroupElement} from "@/models/project/batch.group.element";
import {SimpleSimJob} from "@/models/sim/simple.sim.job";
import {BatchSimJob} from "@/models/sim/batch.sim.job";
import { KPIXElement } from '@/models/project/k.p.i.x.element';

/**
 *
 * @class Project
 *
 * @brief the base class model for a project, we intend to have a few different kinds of projects,
 * such as a library project, a simulation project, a batch simulation project etc.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      March 2020
 *
 */
export class Project extends AbstractProject {

  /** observable to the outside can update presentation of project tree */
  private childAddSubject:        BehaviorSubject<{child: ProjectElement, parent: ProjectElement}>;
  private childAddObservable:     Observable<{child: ProjectElement, parent: ProjectElement}>;

  /** observable to the outside can update presentation of project tree */
  private childDeleteSubject:      BehaviorSubject<ProjectElement[]>;
  private childDeleteObservable:   Observable<ProjectElement[]>;

  /** observable to the outside can redraw/refresh a node */
  private nodeRedrawSubject:      BehaviorSubject<ProjectElement[]>;
  private nodeRedrawObservable:   Observable<ProjectElement[]>;

  /** observable to the outside for when we programmatically expand/collapse a node */
  private nodeExpandSubject:      BehaviorSubject<[ProjectElement[], boolean]>;
  private nodeExpandObservable:   Observable<[ProjectElement[], boolean]>;

  private jobUpdates: Subscription  = null;

  public services:   ServiceManager = null;

  /** the name of the project */
  public name: string               = '';

  /** the kind of project */
  public type: ProjectType          = ProjectType.None;

  /** the icon to use for this project */
  public icon: string               = '';
  public facon: string              = '';

  /** the actual blob (file) where we store/persist the project model */
  public blob: Blob                 = null;

  /** the blob key */
  public key: string                = '';

  /** the blob instance */
  public instance: string           = '';

  /** the elements in this project; they hve an internal parent/child structure */
  public elements: Map<string, ProjectElement> = new Map<string, ProjectElement>();

  /** the elements (result nodes) that might be watching jobs...and need updates */
  public resultElements: Map<string, ProjectElement> = new Map<string, ProjectElement>();

  /**
   *
   * when any child element is doing a long operation, they can insert themselves
   * into the "busy" set.  Operations that should not happen while any child work is
   * ongoing...can check if the busy set is empty or not, and take appropriate action.
   * For example, don't allow deletion of a parent...if anything in "busy" is a
   * descendant.  That is, you can't delete if one of your children is in the middle
   * of doing something.
   *
   */
  public busyElements: Map<string, ProjectElement> = new Map<string, ProjectElement>();

  /** the next id for elements to be added */
  public nextId: number = 1;

  /** flag for when we are already generating a KPI table */
  public kpiInProgress: boolean = false;

  /** projects are marked as deleted once actually deleted, so that other pointers to the project can know */
  public deleted: boolean = false;

  /** track a unique id for KPI tables (persistent across saves) */
  public static kpiTableID: 1;

  /** flip to true, anytime we do something that would require a save of the project. */
  public dirty: boolean   = false;

  /** standard constructor */
  constructor(name: string, icon: string) {

    super();

    this.services    = ServiceLocator.injector.get(ServiceManager);
    this.name        = name;
    this.icon        = icon;

    /* make sure we have a root node */

    this.elements.set('pe-0', new RootElement(this, 'pe-0', this.name, this.icon));

    this.childAddSubject        = new BehaviorSubject<{child: ProjectElement, parent: ProjectElement}>(null);
    this.childAddObservable     = this.childAddSubject.asObservable();

    this.childDeleteSubject     = new BehaviorSubject<ProjectElement[]>([]);
    this.childDeleteObservable  = this.childDeleteSubject.asObservable();

    this.nodeRedrawSubject     = new BehaviorSubject<ProjectElement[]>([]);
    this.nodeRedrawObservable  = this.nodeRedrawSubject.asObservable();

    this.nodeExpandSubject     = new BehaviorSubject<[ProjectElement[], boolean]>(null);
    this.nodeExpandObservable  = this.nodeExpandSubject.asObservable();

    this.jobUpdates = this.services.watcher.updates().subscribe((jobs: SimulationJob[]) => {

      jobs.forEach((job: SimulationJob) => {

        if (job.type === SimulationJobType.SIMPLE_SIM) {

          /* the simple sim jobs construct their update/tracking id by prefixing the job id with 'linesim-' */

          if ( !this.resultElements.has(job.simKey()) ) {

            /* not one in this project */

            return;
          }

          /* redraw! */

          const el: LineSimJobElement = this.resultElements.get(job.simKey()) as LineSimJobElement;

          /* the model (the job object) is the same one that the watcher updates, so just redraw */

          el.update();

        } else if (job.type === SimulationJobType.BATCH_SIM) {

          /* batch simulation */

          if ( !this.resultElements.has(job.simKey()) ) {

            /* not one in this project */

            return;
          }

          /* redraw! */

          const el: BatchSimJobElement = this.resultElements.get(job.simKey()) as BatchSimJobElement;

          /* the model (the job object) is the same one that the watcher updates, so just redraw */

          el.update();
        }

      });

    });

  }

  /** elements self register when they need to watch job updates */
  public registerJob(job: SimpleSimJob|BatchSimJob, element: ProjectElement) {

    /* track it for updates */

    this.resultElements.set(job.simKey(), element);

    /* make sure the job watcher has it */

    if (element instanceof LineSimJobElement) {

      /* on demand single sim */

      const lsje: LineSimJobElement = element as LineSimJobElement;

      this.services.watcher.monitor([lsje._job]);

    } else if (element instanceof BatchSimJobElement) {

      /* batch sim, 10's or 100's of sims */

      const bsje: BatchSimJobElement = element as BatchSimJobElement;

      this.services.watcher.monitor([bsje._job]);
    }
  }

  public unregisterJob(job: SimpleSimJob|BatchSimJob, element: ProjectElement) {

    this.resultElements.delete(job.simKey());
  }

  /** watch the child additions */
  public onAddChild(): Observable<{child: ProjectElement, parent: ProjectElement}> {

    return this.childAddObservable;
  }

  /** watch the child deletions */
  public onDeleteChild(): Observable<ProjectElement[]> {

    return this.childDeleteObservable;
  }

  /** watch the child redraws */
  public onRedrawChild(): Observable<ProjectElement[]> {

    return this.nodeRedrawObservable;
  }

  /** watch the child collapse/expand */
  public onExpandChild(): Observable<[ProjectElement[], boolean]> {

    return this.nodeExpandObservable;
  }

  /** helper to get directory of the project file */
  public dirname(): string {

    /* try to automatically handle the early project structure we were using for a while... */

    const oldStyle = `/projects/project-${this.name}`;

    if (this.key.lastIndexOf(oldStyle) > 0) {

      const prefix: string = this.key.substring(0, this.key.lastIndexOf(oldStyle));

      return `${prefix}/projects/${this.name}`;
    }

    /* normal project structure ... */

    let base: string = this.key;

    if ( empty(base) ) {

      /* ? all projects should have a project file when created */

      return null;
    }

    if (base.lastIndexOf('/') <= 0) {

      return '/';
    }

    return base.substring(0, base.lastIndexOf('/'));
  }

  /** fetch base name of the project file */
  public basename(): string {

    let base: string = this.key;

    if ( empty(base) ) {

      base = this.name;
    }

    return base.substring(base.lastIndexOf('/') + 1);
  }

  /** convenience to rename the project */
  public renameProject() {

    this.root().renameProject();
  }

  /** finalize the move of a project */
  public move(newKey: string, newName: string) {

    const project: Project = this;

    Logger.debug(`[Project] renaming project: `, newKey, ` => `, newName);

    /*
     * a new project name was validated, and the user's project history was updated to
     * reflect the project name change (and the implied project key change), now all
     * we have to do is twitch the key paths of any blobs in the project, so that
     * everything is relative to the new project name; because the project name is also
     * used as the project folder in the user's projects area.
     *
     * Basically, before moving a project you must have already done any other book keeping
     * that was necessary, this method just handles the "internal" part of moving a project.
     *
     */

    /* figure out what the search and replace is on the blob paths for the children... */

    const  search: string = project.dirname();
    const replace: string = dirname(newKey);
    const  oldKey: string = project.key;

    project.key           = newKey;
    project.name          = newName;
    project.root()._label = this.name;

    project.root().redraw();

    /* now adjust the paths of all the children in bulk and save again at the end to finalize... */

    project.root().shiftBlobs(search, replace, (result: any) => {

      if (result !== true) {

        /* there was a problem! */

        Logger.error(`[Project] could not properly rename project: `, result);

        return ;
      }

      /*
       * all elements have been relocated as needed, and re-key'd as needed,
       * save the updated project file...
       *
       */

      /* un-link the old project file, its not needed now... */

      Logger.debug('[Project] removing old project file...');

      project.services.blobber.remove(oldKey)
        .pipe(first())
        .subscribe(
          () => {

            Logger.debug('[Project] renaming complete, old project file removed..');

            Logger.debug('[Project] re-saving (create)...');

            this.services.projects.newProject(this);

          },
          (error) => {

            Logger.error('[Project] problems renaming project: ', error);
          });
    });
  }

  /** delete the project */
  public delete() {

    Logger.debug('[Project] deleting project: ', this.key);

    /* do the internal element tree delete, also delete the blob that actually is the project file */

    this.root().deleteElements(this.root().children(), true, [this.key]);
  }

  /** force a redraw of the given elements (i.e. after icon change) */
  public redrawElements(children: ProjectElement[]): void {

    if (children.length == 0) {

      /* nothing to do */

      return ;
    }

    this.nodeRedrawSubject.next(children);
  }

  /** if the given children are not expanded, expand them */
  public expand(children: ProjectElement[]): void {

    Logger.debug(`[Project] expanding: `, children);

    this.nodeExpandSubject.next([children, true]);
  }

  /** if the given children are not expanded, expand them */
  public collapse(children: ProjectElement[]): void {

    Logger.debug(`[Project] collapsing: `, children);

    this.nodeExpandSubject.next([children, false]);
  }

  /** delete the given children from the project */
  public deleteElements(
    children: ProjectElement[],
    publish?: boolean
  ) {

    Logger.debug(`[Project] deleting: `, children, publish);

    if (this.elements.size === 0) {

      Logger.debug(`[Project] empty project, skipping delete.`);

      return true;
    }

    /*
     * for deleting all descendants, the caller is responsible for building out the complete
     * set of 'children'.  Here we just do the bulk delete.
     *
     */

    const deleted: ProjectElement[] = [];

    /* unlink the children */

    children.forEach(child => {

      const     id: string         = child._id;
      const parent: ProjectElement = child._parent;

      if (!parent) {

        return ;
      }

      /* unlink */

      parent._children = parent._children.filter(x => x._id !== id);

      /* remove from project element set */

      this.elements.delete(id);
      deleted.push(child);

    });

    if (publish) {

      /* publish the tree change */

      this.childDeleteSubject.next(deleted);
    }

    this.forcedSave();

    return true;
  }

  /** consistent way of generating default project folder path from project name and user who owns it */
  public defaultProjectFolder(userName: string, projectName) {

    return `/users/${userName}/projects/${projectName}`;
  }

  /** consistent way of generating a project file path from a project name and the user who owns it. */
  public defaultProjectFile(userName: string, projectName) {

    return `/users/${userName}/projects/${projectName}/.${projectName}.json`;
  }

  /** add the given child to the project */
  public addElement(
    child: ProjectElement,
    parent?: ProjectElement,
    publish?: boolean
  ) {

    const id: string = 'pe-' + this.nextId as string;

    this.nextId++;

    if (!child._id || (child._id === '0') || (child._id === '')) {

      child._id = id;

      /* add child to managed elements */

      this.elements.set(id, child);

    }

    /* double check for re-parenting */

    if (child._parent) {

      /* unlink from existing parent */

      child._parent._children = child._parent._children.filter(x => x._id !== child._id);
      child._parent = null;
    }

    /* if we were given a parent, think link them up */

    if (parent) {

      child._parent = parent;

      if (!parent._children.find(x => x._id === child._id)) {

        parent._children.push(child);

        /* tell the child its been hooked into the tree */

        child.onAdded();
      }

      parent.orderChildren();

      child._expanded = true;

      if (publish) {

        /* publish the tree change */

        this.childAddSubject.next({child, parent});
      }
    }

  }

  /** use the given KPI elements to add a KPI table element to the given results parent node */
  public kpiTableFinalize(results: ResultsElement, kpiNodes: Map<string, KPIXElement>) {

    Logger.debug(`[Project] finalizing KPI table element`, kpiNodes);

    /*
     * pre-scan the KPI elements, if any of them actually failed to generate KPI data... we
     * have to stop, because we can't generate a table that would silently include bad results.
     *
     */

    let badKPI: KPIXElement = null;

    for( const el of kpiNodes.values()) {

      if (!el.hasData()) {

        badKPI = el;
        break;
      }

    }

    if (badKPI !== null) {

      /* at least one of the current KPI items is missing data */

      Logger.error(`[Project] at least one of the current KPIX is missing its data: `, badKPI);

      this.kpiInProgress = false;

      return ;
    }

    /* accumulate the contents into one big object that we can use to create a KPI table with... */

    const observables:   any[]       = [];
    const     project: Project       = this;
    const     tracker: KPIXElement[] = [];

    for (const kpiElement of kpiNodes.values()) {

      if (kpiElement === null) {

        continue;
      }

      Logger.debug(`[Project] . queuing read of KPIX: `,  kpiElement._kpixKey);

      let obs: Observable<Object> = project.services.blobber.contents( kpiElement._kpixKey, 'latest').pipe(first());

      /*
       * Note: forkJoin (in rxjs) is specified to emit its results in the exact same order as the observables,
       * when you give it an array of observables.  So to map the results back to the KPIXElement for a given
       * result, we can just use the same index in each array...as long as they are in the same order.
       *
       * https://rxjs.dev/api/index/function/forkJoin
       *
       */

      tracker.push(kpiElement);

      observables.push(obs);
    }

    /* now fork & join to do them all in parallel */

    forkJoin(observables).subscribe({

      next: (kpiBlobs: any[]): void => {

        const data: any[]    = [];
        const kpiData: KPI[] = [];

        kpiBlobs.forEach((item: any): void => {

          data.push(JSON.parse(item));
        });

        let resultIndex = 0;

        data.forEach((item: any): void => {

          /* map back to the KPIXElement so we can know the input/output/kpix blob keys */

          let kpix: KPIXElement = tracker[resultIndex];

          item.input            = kpix._inputKey;
          item.output           = kpix._outputKey;
          item.key              = kpix._kpixKey
          item.label            = kpix._label;

          resultIndex++;

          /* append this item for the table */

          kpiData.push(new KPI(item));

        });

        /* Create a KPITableElement using the KPI data, first we need a UUID ... */

        project.services.uuid.generate()
          .pipe(first())
          .subscribe({
            next: (uuidData: any): void => {

              /* figure out the kpi table blob location */

              const uuid: string          = uuidData.guid;
              const projectFolder: string = project.dirname();
              const kpiFolder: string     = projectFolder + `/kpi-table/${uuid}/`;
              const kpiKey: string        = kpiFolder + 'table.kpt';

              Logger.debug('[Project] generating KPI Table: ', kpiKey, kpiData);

              const el: KPITableElement = ProjectElementFactory.fromObject(
                ProjectElementType.KPITable,
                {
                  project:  project,
                  id:       '',
                  instance: Project.kpiTableID,
                  data:     kpiData,
                  created:  new Date(),
                  tablekey: kpiKey,
                  parent:   results,
                  publish:  true
                }) as KPITableElement;

              /* make sure the results list is open so they can see the new table element */

              project.expand([results]);

              Project.kpiTableID++;

              /* trigger a save */

              project.forcedSave();

              project.kpiInProgress = false;

              /* auto-launch the KPI table editor on the table we just created */

              el.action('editkpitable', [el]);

            },

            error: (err: any): void => {

              Logger.error(`[Project] could not generate UUID for KPI Table?`, errorAsText(err));

              project.kpiInProgress = false;

            }

          });
      },

      error: (err: any): void => {

        Logger.error(`[Project] could not read KPI blobs?`, errorAsText(err));

        project.kpiInProgress = false;

      }

    });
  }

  /** fetch all the possible KPI rollups within the scope of the current project */
  public allRollupKPI(): {label: string, bob: BatchGroupElement}[] {

    const pe: ProjectElement                    = this.root();

    if (!pe) {

      return [];
    }

    /* ok, filter the tree for "batch of batch" elements ... */

    const    kids: ProjectElement[]               = pe.descendants(ProjectElementType.BatchGroup);

    /* now for each hit, make a human-readable label to go with the element */

    const seen: Map<string, BatchGroupElement> = new Map<string, BatchGroupElement>();

    kids.forEach((el: ProjectElement) => {

      if (!(el instanceof BatchGroupElement)) {

        return;
      }

      const bob: BatchGroupElement = el as BatchGroupElement;

      /* only keep the first one we find for each corridor */

      if (seen.has(bob._label)) {

        return ;
      }

      seen.set(bob._label, bob);
    });

    /* pass 'em back */

    return Array.from(seen.values()).map(
      (x: BatchGroupElement) => {
        return {
          label: x._label,
          bob: x
        }
      });
  }

  /** fetch all the possible regressions within the scope of the current project */
  public allRegressions(): {label: string, key: string}[] {

    const pe: ProjectElement                    = this.root();

    if (!pe) {

      return [];
    }

    /* ok, filter the tree for regression elements ... */

    const    kids: ProjectElement[]               = pe.descendants(ProjectElementType.RegressionResult);

    /* now for each hit, make a human-readable liable to go with the actual KPI rollup blob key */

    const seen: Map<string, RegressionElement> = new Map<string, RegressionElement>();

    kids.forEach((el: ProjectElement) => {

      if (!(el instanceof RegressionElement)) {

        return;
      }

      const re: RegressionElement = el as RegressionElement;

      /* only keep the first one we find for each corridor */

      if (seen.has(re._parent._label)) {

        return ;
      }

      seen.set(re._parent._label, re);
    });

    /* pass 'em back */

    return Array.from(seen.values()).map(
      (x: RegressionElement) => {
        return {
          label: x._parent._label + ' ' + x._label,
            key: x._blob
        }
      });
  }

  /** on any element where you can trigger the generation of a KPI table...add one to the results branch */
  public generateKPITable(child: ProjectElement) {

    /*
     * Note: May 5th, 2025 - we are dropping KPI and just keeping the KPIX artifact,
     * since it has everything a KPI has.  We still have to be backwards compatible so
     * we can't remove KPI support...but we want to now stop adding KPI elements to
     * the project tree. https://smartnetwork.atlassian.net/browse/SN-1751
     *
     */

    if (this.kpiInProgress) {

      /* already generating */

      return ;
    }

    this.kpiInProgress = true;

    /* where to save the table? */

    const       group: SimulationGroupElement = this.simulationGroup(child);
    const resultsNode: ResultsElement       = this.results(child);
    const     project: Project = this;

    if (resultsNode === null) {

      /* not enclosed by and simulation? */

      project.kpiInProgress = false;

      return ;
    }

    Logger.debug(`[Project] generating KPIX table #${Project.kpiTableID} via `, child, ` saving to:`, resultsNode);

    /* sweep the project tree for KPI elements we already have */

    Logger.debug(`[Project] looking for existing and missing KPIX...`);

    /* sweep the project tree for KPI elements we need to create (i.e. results with no KPI child) */

    /* where are the result nodes ? */

    const    kids: ProjectElement[]               = group.descendants(ProjectElementType.LineSimResult);
    const results: Map<string, LineSimJobElement> = new Map<string, LineSimJobElement>();
    const    todo: Map<string, LineSimJobElement> = new Map<string, LineSimJobElement>();
    const pending: Map<string, KPIXElement>        = new Map<string, KPIXElement>();
    const   ready: Map<string, KPIXElement>        = new Map<string, KPIXElement>();

    kids.forEach((el: ProjectElement) => {

      if ( !(el instanceof LineSimJobElement)) {

        return ;
      }

      results.set(el._id, el as LineSimJobElement);

      /* does it already have a KPI? */

      const hits: ProjectElement[] = el.children().filter((x: ProjectElement) => {

        if (x._type !== ProjectElementType.KPIX) {

          return false;
        }

        /* at least one of them must have a success status */

        const kpi: KPIXElement = x as KPIXElement;

        if (kpi.completed() === false) {

          return false;
        }

        /* it's a KPIX element, and it completed ok (we have data) */

        return true;
      });

      if (hits.length != 1) {

        /* we have to generate the KPI for this one before we can continue */

        const item: LineSimJobElement = el as LineSimJobElement;

        /* queue it up, we key on output key because its unique/persistent for that job */

        todo.set(item._job.localOutputKey, item);

        /*
         * create the KPI elements in advance, even though they aren't actually usable yet...
         * so we can show the user that its in the process of being created.
         *
         */

        const kpiEl: KPIXElement = ProjectElementFactory.fromObject(
          ProjectElementType.KPIX, {
            project: project,
            id:      '',
            input:   '',
            output:  '',
            kpi:     '',
            kind:    ProjectElementType.KPIX,
            parent:  item,
            publish: true
          }) as KPIXElement;

        kpiEl.setStatus('Pending...', 'pending');

        pending.set(item._job.localOutputKey, kpiEl);

      } else {

        /* just use the existing KPIX */

        ready.set(hits[0]._id, hits[0] as KPIXElement);
      }

    });

    this.forcedSave();

    Logger.debug(`[Project] found ${results.size} result nodes and ${ready.size} KPIX ready, and ${todo.size} to auto-generate.`);

    /* ensure we have a complete set of KPI before we try to actually create the KPI table */

    if (todo.size > 0) {

      /* now use fork/join to auto create the triplets for each result where we have to fill in a KPI */

      const projectFolder: string = project.dirname();
      const   observables:  any[] = [];

      for (const jobResult of todo.values()) {

        if ((jobResult._job === null) || (empty(jobResult._job.localOutputKey))) {

          /* this job isn't ready to generate KPI yet */

          Logger.warning(`[Project] job result ${jobResult._label} is not ready to make KPIX yet, skipping.`);

          continue;
        }

        const      uuid: string = jobResult._job.uuid();
        const kpiFolder: string = projectFolder + `/kpix/${uuid}/`;
        const      kpix: string = kpiFolder + jobResult._parent.basename() + '.kpix';
        const     input: string = jobResult._parent._blobKey;
        const    output: string = jobResult._job.localOutputKey;

        /* as we copy we are also forcing the type to be whatever the user selected as the import type */

        Logger.debug(`[Project] . queuing KPIX: `, input, output, kpix);

        observables.push(project.services.kpi.kpix(input, output, kpix).pipe(catchError((error: any)=> {

          Logger.error(`[Project] auto-KPIX error: `, error);

          if (!('url' in error)) {

            Logger.error(`[Project] auto-KPIX error 'url' is missing in error: `, error);

            return of(false);
          }

          if (!('error' in error)) {

            Logger.error(`[Project] auto-KPIX error 'error' is missing in error: `, error);

            return of(false);
          }

          /*
           * If the KPI's fail for some reason, try to intercept the failure and substitute an object
           * that our success code can see is an error, mixed in with the proper results...this allows
           * us to still keep the ones that succeeded, and label the ones that failed with an error.
           *
           * That in turn...allows a user to just keep trying to generate, until they get a complete
           * set of KPIs.
           *
           * If we don't intercept, then forkJoin is an all or nothing deal.  If our environment is
           * generating errors (for whatever reason), it will then be difficult to get a complete
           * set of results.
           *
           */

          let   kpiEl: KPIXElement;
          const   url: URL        = new URL(error.url);
          const  oKey: string     = url.searchParams.get('output');

          kpiEl = pending.get(oKey);

          return of({
            isError: true,
            error,
            text: errorAsText(error),
            element: kpiEl});

        })).pipe(first()));

      }

      Logger.debug(`[Project] ${observables.length} KPIX creations queued.`);

      /*
       * NOTE: to avoid endlessly triggering project saves, because we're generating
       * events for expanding nodes (when they aren't expanded already), we use the skipSave()
       * method below to mark nodes as shippable; since we're going to save the project at
       * the end of the fork and join anyway.
       *
       */

      /* now fork & join to do them all in parallel */

      forkJoin(observables).subscribe({
        next: (results: any[]): void => {

          /* separate the success from the failures (allow for partial completion of the batch ... */

          const errors: any[] = results.filter(x => 'isError' in x);

          results = results.filter(x => 'output' in x);

          /* process the ones that failed */

          errors.forEach((error: any) => {

            const kpiEl: KPIXElement = error.element;
            const errMsg: string     = error.text;

            kpiEl.setStatus(errMsg, 'failed');

            kpiEl._parent.skipSave(true);
            project.expand([kpiEl._parent]);

          });

          /* process the ones that worked... */

          results.forEach((triplet: any) => {

            /* for each triplet, we can create a child node of its result */

            Logger.debug(`[Project] kpix blob created, saving co-ords`, triplet);

            /* find the job that triggered this one */

            const jobResult: LineSimJobElement = todo.get(triplet.output);
            const kpixEl: KPIXElement          = pending.get(triplet.output);

            if (jobResult === null) {

              Logger.error(`[Project] can't find job for KPIX by output `, triplet.output, ', skipping.');

              return;
            }

            /* make sure the KPI we just created is visible */

            jobResult._parent.skipSave(true);
            project.expand([jobResult]);

            /* mark the KPI as being ready for use */

            kpixEl._inputKey  = triplet.input;
            kpixEl._outputKey = triplet.output;
            kpixEl._kpixKey   = triplet.kpix;

            kpixEl.setStatus('', 'completed');

            ready.set(kpixEl._id, kpixEl as KPIXElement);

          });

          if (errors.length > 0) {

            Logger.error(`[Project] can't finalize KPIX table; some KPIX are in error: `, errors);

            project.kpiInProgress = false;

            project.forcedSave();

          } else {

            project.forcedSave();

            /* now that all the KPI are present, we can finalize the KP table */

            project.kpiTableFinalize(resultsNode, ready);
          }
        },
        error: (err: any): void => {

          /*
           * try to locate the specific KPI element that failed...so we can mark that one as the
           * the on that failed, with its specific error message.
           *
           */

          let kpiEl: KPIXElement;
          const url: URL = new URL(err.url);
          const oKey: string = url.searchParams.get('output');

          kpiEl = pending.get(oKey);

          if (kpiEl instanceof KPIXElement) {

            let errMsg: string = errorAsText(err);

            kpiEl.setStatus(errMsg, 'failed');

            kpiEl._parent.skipSave(true);
            project.expand([kpiEl._parent]);
          }

          /* mark all the KPI we tried to make as failed ... */

          for (const el of pending.values()) {

            if (el === kpiEl) {

              /* we already set the error (above) */

              continue;
            }

            if (el._kpiStatus === 'failed') {

              /* already marked as failed. */

              continue;
            }

            el.setStatus('Failed!', 'failed');

            el._parent.skipSave(true);
            project.expand([el._parent]);
          }

          project.kpiInProgress = false;

          project.forcedSave();

          Logger.error(`[Project] error trying to process KPI`, err);

        }

      });

    } else {

      /* just finalize the KPI table */

      project.kpiTableFinalize(resultsNode, ready);
    }
  }

  /** fetch the simulation group this element is in */
  public simulationGroup(child: ProjectElement): SimulationGroupElement {

    if ((typeof child === 'undefined') || (child === null)) {

      return null;
    }

    /* walk up the Simulation node ... */

    let a: ProjectElement = child;

    while (a !== null) {

      if (a._type === ProjectElementType.SimulationGroup) {

        break;
      }

      /* keep looking */

      a = a._parent;
    }

    if ((a === null) || (a.children().length === 0)) {

      /* it's missing the inputs/results branches */

      return null;
    }

    return a as SimulationGroupElement;
  }

  /** fetch the results branch / bucket for whatever simulation tree this element is in */
  public results(child: ProjectElement): ResultsElement {

    /* fetch the parent simulation group ... */

    const a: SimulationGroupElement = this.simulationGroup(child);

    if ((a === null) || (a.children().length === 0)) {

      /* its missing the inputs/results branches */

      return null;
    }

    /* find the results branch ... */

    const hits: ProjectElement[] = a.children().filter(x => x._type === ProjectElementType.Results);

    if (hits.length !== 1) {

      return null;
    }

    /* pass it back */

    return hits[0] as ResultsElement;
  }

  /** fetch one of the root elements, the Locomotive Models branch */
  public locomotiveModels() : SimulationElement {

    /* we want one of the main children under the root */

    let pe: ProjectElement = this.root();

    if (!pe) {

      return null;
    }

    const hits: ProjectElement[] = pe.children().filter(x => x._type === ProjectElementType.LocomotiveModel);

    if (hits.length == 0) {

      return null;
    }

    return hits[0] as SimulationElement;
  }

  public batchSummaries() : BatchSummariesElement {

    const oe: OperationalMeasuresElement = this.operationalMeasures();

    if (!oe) {

      return null;
    }

    const hits: ProjectElement[] = oe.children().filter(x => x._type === ProjectElementType.BatchSummaries);

    if (hits.length == 0) {

      /* if we don't have one already, create one ... */

      const e: ProjectElement = new BatchSummariesElement(this, '', oe);

      hits.push(e);
    }

    return hits[0] as BatchSummariesElement;
  }

  /** fetch one of the root elements, the Operational Measures branch */
  public operationalMeasures() : OperationalMeasuresElement {

    /* we want one of the main children under the root */

    let pe: ProjectElement = this.root();

    if (!pe) {

      return null;
    }

    const hits: ProjectElement[] = pe.children().filter(x => x._type === ProjectElementType.OperationalMeasures);

    if (hits.length == 0) {

      return null;
    }

    return hits[0] as OperationalMeasuresElement;
  }

  /** fetch one of the root elements, the Simulation branch */
  public simulation() : SimulationElement {

    /* we want one of the main children under the root */

    let pe: ProjectElement = this.root();

    if (!pe) {

      return null;
    }

    const hits: ProjectElement[] = pe.children().filter(x => x._type === ProjectElementType.Simulation);

    if (hits.length == 0) {

      return null;
    }

    return hits[0] as SimulationElement;
  }

  /** fetch the root element for the project */
  public root() : ProjectElement {

    return this.child('pe-0');
  }

  public forcedSave(): void {

    if (this.elements.size === 0) {

      /* nothing to save. */

      return ;
    }

    /*
     * save the modified project, save via project element so we force the project
     * to be marked as dirty, indirectly forcing a save
     *
     */

    (this.elements.values().next().value as ProjectElement).save();

  }

  public save() {

    if (this.deleted === true) {

      /* it's not valid any longer */

      return ;
    }

    if (this.dirty) {

      Logger.debug(`[Project] project modified, queuing save.`);

      this.services.projects.saveProject(this);

      /* flip back to unmodified. */

      this.dirty = false;

    } else {

      Logger.debug(`[Project] project not modified yet, skipping save.`);
    }
  }

  /** fetch a project element (random access) */
  public child(id: string) : ProjectElement {

    if (this.elements.has(id)) {

      return this.elements.get(id);
    }

    /* no such child */

    return null;
  }

  /** snapshot this project into a JSON string */
  public serialize() : string {

    /* convert to simple nodes */

    const  root:   ProjectElement = this.root();
    const  kids: ProjectElement[] = root.descendants();
    const nodes:            any[] = [];

    for (const k of kids) {

      nodes.push(k.node());
    }

    /* stringify */

    return JSON.stringify({
          name: this.name,
          icon: this.icon,
           key: this.key,
      instance: this.instance,
         facon: this.facon,
          type: stringOfProjectType(this.type),
          next: this.nextId,
    kpitableid: Project.kpiTableID,
         nodes
    });
  }

  /** fill this project from the given JSON */
  public deserialize(json: string) : boolean {

    /* start with nothing */

    this.elements.clear();

    /* parse out the simple objects from the saved text */

    const data = JSON.parse(json);

    this.name     = data.name;
    this.icon     = data.icon;
    this.type     = projectTypeOfString(data.type);
    this.key      = data.key;
    this.instance = data.instance;
    this.facon    = data.facon;
    this.nextId   = data.next;

    /* only newer projects will have this attribute */

    Project.kpiTableID = 1;

    if ( isset(data.kpitableid)) {

      Project.kpiTableID = data.kpitableid;
    }

    /* convert simple nodes back to elements, and re-build the element tree */

    const nodes: any = data.nodes;
    const self: Project = this;

    /* first re-insert all nodes (without parent/children linkage */

    for (const n of nodes) {

      const options = {
        project: self,
        id:      n.id,
        name:    n.label,
        kind:    n.dimKind,
        parent:  null,
        publish: false,
        type:    n.blobType,
        key:     n.blobKey,
        icon:    n.icon
      };

      const peType: ProjectElementType = ProjectElement.typeofString(n.type);
      const pe:     ProjectElement     = ProjectElementFactory.fromObject(peType, options) as ProjectElement;

      if (pe === null) {

        /* bad node type, skip it */

        continue ;
      }

      pe.fromNode(n);

      self.elements.set(n.id, pe);

    }

    /* now scan again, but we can use parent id to rebuild the tree, because all ids are restored */

    for (const m of nodes) {

      const child: ProjectElement = self.elements.get(m.id);

      if ((!m.parent) || (!child)) {

        /* nothing to do */

        continue ;
      }

      child._parent = self.elements.get(m.parent);

      if (child._parent._children.find(x => x._id === child._id)) {

        /* its already there */

        continue ;
      }

      /* add the child to the parent's children */

      child._parent._children.push(child);

      /* tell the child it's been hooked into the tree */

      child.onAdded();
    }

    /*
     * if any elements registered themselves as job watchers...they also would have been
     * been added to the job watcher...so we shouldn't have to do a global project sweep
     * to pick them up, they should already be being watched.
     *
     */


    /* project is fully restored */

    return true;
  }

  /** generate JSON style tree for FancyTree display */
  public render(event, data) {

    const el: ProjectElement = this.root();

    /* if the event/data identifies something other than root...jump to that node */

    /* TODO: */

    /* render! */

    return el.render(event, data);
  }
}
